import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { Virtuoso } from 'react-virtuoso';
import { twMerge } from 'tailwind-merge';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { fadeInAnimation } from '@components/Feed/ListViews/components/shared';
import { NewNotificationButton } from '@components/Notifications/NewNotificationButton';
import { NotificationLoadingSkeleton } from '@components/Notifications/shared';
import { useUserNotificationStatus } from '@components/Notifications/useUserNotificationStatus';
import { useUserNotifications } from '@components/Notifications/useUserNotifications';
import { RecommendedFollowsSkeleton } from '@components/RecommendedFollows';
import { Seo } from '@components/Seo';
import { TwoColumnPage } from '@components/TwoColumnPage';
import { TwoColumnPageHeader } from '@components/TwoColumnPage/Header';
import {
  AUDIO_PLAYING_MOBILE_POPUP_BUTTON_OFFSET,
  AUDIO_PLAYING_TABLET_POPUP_BUTTON_OFFSET,
  MOBILE_POPUP_BUTTON_OFFSET,
  TABLET_POPUP_BUTTON_OFFSET,
} from '@components/basics/PopupButton';
import { MediaQuery } from '@context/mediaQuery';
import { useIsSsr } from '@hooks/useIsSsr';
import { useAudioController } from '@store/audio/AudioController';
import { wait } from '@utils/helpers';

/**
 * Dynamically importing these components we reduce bundle size
 * browser will only fetch the code for these components when they are needed
 */

// other components
const EmptyNotifications = dynamic(
  () => import('@components/Notifications/EmptyNotifications').then(c => c.EmptyNotifications),
  {
    ssr: false,
  },
);
const PeopleToFollow = dynamic(
  () => import('@components/RecommendedFollows').then(c => c.RecommendedFollows),
  { ssr: false, loading: () => <RecommendedFollowsSkeleton /> },
);
//general notifications
const ArtistDrop = dynamic(
  () => import('@components/Notifications/NotificationCards/ArtistDrop').then(c => c.ArtistDrop),
  {
    ssr: false,
    loading: () => <NotificationLoadingSkeleton length={1} />,
  },
);
const GoldenEggWinnerCard = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/GoldenEggWinner').then(
      c => c.GoldenEggWinnerCard,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} /> },
);
const NewFollowerCard = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/NewFollower').then(c => c.NewFollowerCard),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} avatar /> },
);
const PresaleArtistDrop = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/PresaleArtistDrop').then(
      c => c.PresaleArtistDrop,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} /> },
);
const ReferralReceivedCard = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/ReferralReceived').then(
      c => c.ReferralReceivedCard,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} /> },
);
const SubscribeCard = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/SubscribeCard').then(c => c.SubscribeCard),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} /> },
);
const ReleaseMinted = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/ReleaseMinted').then(
      c => c.ReleaseMintedCard,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} avatar /> },
);
const ViralMintsReachedCard = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/ViralMintCard').then(c => c.ViralMintCard),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} /> },
);

//post notifications
const NewPostComment = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/Posts/NewPostComment').then(
      c => c.NewPostCommentCard,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} avatar /> },
);

const PostUpvoted = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/Posts/PostUpvoted').then(
      c => c.PostUpvotedCard,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} avatar /> },
);

const ArtistReleasePostedCard = dynamic(
  () =>
    import('@components/Notifications/NotificationCards/Posts/ArtistReleasePosted').then(
      c => c.ArtistReleasePosted,
    ),
  { ssr: false, loading: () => <NotificationLoadingSkeleton length={1} avatar /> },
);

function renderItem(_: number, data: ReturnType<typeof useUserNotifications>['data'][0]) {
  // need these div wrappers because since we are lazy loading the components they initially render as null which causes the virtuoso to throw zero height error
  switch (data.__typename) {
    case 'SubscribeToEmailCTA':
      return (
        <motion.div
          initial={{ opacity: 0, minHeight: 64 }}
          animate={{ opacity: 1, minHeight: 64 }}
          exit={{ opacity: 0, minHeight: 0 }}
          key={data.id}
        >
          <SubscribeCard />
        </motion.div>
      );
    case 'NewFollower':
      return <NewFollowerCard notificationFrag={data} />;
    case 'ReferralReceivedNotification':
      return <ReferralReceivedCard notificationFrag={data} />;
    case 'GoldenEggWon':
      return <GoldenEggWinnerCard notificationFrag={data} />;
    case 'PresaleStarted':
      return <PresaleArtistDrop notificationFrag={data} />;
    case 'PublicSaleStarted':
      return <ArtistDrop notificationFrag={data} />;
    case 'ViralMintsReached':
      return <ViralMintsReachedCard notificationFrag={data} />;
    case 'ReleaseMinted':
      return <ReleaseMinted notificationFrag={data} />;
    case 'NewPostComment':
      return <NewPostComment notificationFrag={data} />;
    case 'NewPostUpvote':
      return <PostUpvoted notificationFrag={data} />;
    case 'ArtistReleasePosted':
      return <ArtistReleasePostedCard notifFragment={data} />;
    default:
      return null;
  }
}

export default function Notifications() {
  const { isInitialLoading, data, loadMore, hasNextPage, refetch } = useUserNotifications();

  const [isFetchingNewNotifs, setIsFetchingNewNotifs] = React.useState(false);

  const isSsr = useIsSsr();
  const { tablet } = MediaQuery.useContainer();
  const { activeTrackId, hideAudioPlayer } = useAudioController();
  const memoizedFooter = React.useMemo(
    () => (hasNextPage ? <NotificationLoadingSkeleton length={15} /> : null),
    [hasNextPage],
  );
  const { mutateNotificationStatus, markNotificationsAsRead, hasUnreadNotifications } =
    useUserNotificationStatus();

  React.useEffect(() => {
    // let the BE know that the user has seen the notifications
    mutateNotificationStatus({});
  }, [mutateNotificationStatus]);

  const mobileOffset =
    activeTrackId && !hideAudioPlayer
      ? AUDIO_PLAYING_MOBILE_POPUP_BUTTON_OFFSET
      : MOBILE_POPUP_BUTTON_OFFSET;

  const tabletOffset =
    activeTrackId && !hideAudioPlayer
      ? AUDIO_PLAYING_TABLET_POPUP_BUTTON_OFFSET
      : TABLET_POPUP_BUTTON_OFFSET;

  const newNotifsBottomOffset = tablet ? tabletOffset : mobileOffset;

  const handleFetchNewNotification = useStableCallback(async () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await wait(200);
    setIsFetchingNewNotifs(true);
    await wait(50);
    window.scrollTo({ top: 0 });
    await refetch();
    markNotificationsAsRead();
    window.scrollTo({ top: 0 });
    setIsFetchingNewNotifs(false);
  });

  const isEmpty = data?.length === 0 && !isInitialLoading;
  return (
    <>
      <Seo title="Notifications" />
      <TwoColumnPage
        leftContent={
          <div className="flex  w-full flex-1 flex-col">
            <TwoColumnPageHeader backFallbackUrl="/" leftContent="Notifications" />

            <div
              className={twMerge(
                'z-above1 flex w-full flex-col',
                isEmpty ? 'overflow-visible' : 'overflow-hidden',
              )}
            >
              <React.Suspense fallback={<NotificationLoadingSkeleton length={15} />}>
                {isEmpty ? (
                  <EmptyNotifications />
                ) : (
                  <motion.div
                    {...fadeInAnimation({
                      duration: 0.5,
                      delay: 0.2,
                    })}
                  >
                    <AnimatePresence>
                      <Virtuoso
                        useWindowScroll
                        data={data || []}
                        itemContent={renderItem}
                        endReached={loadMore}
                        components={{
                          Footer: () => memoizedFooter,
                        }}
                        style={{ willChange: 'height' }}
                        increaseViewportBy={100}
                      />
                    </AnimatePresence>
                  </motion.div>
                )}
              </React.Suspense>
            </div>
            {!isSsr ? (
              <NewNotificationButton
                isVisible={hasUnreadNotifications}
                onClick={handleFetchNewNotification}
                bottom={newNotifsBottomOffset}
                loading={isFetchingNewNotifs}
              />
            ) : null}
          </div>
        }
        rightContent={<PeopleToFollow />}
      />
    </>
  );
}
